import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { Usuario } from '../types';
import authService from '../services/authService';
import pollingService from '../services/PollingService';
import api from '../services/api';

interface AuthContextType {
  usuario: Usuario | null;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  verificandoAutenticacao: boolean;
  atualizarPerfil: (dadosAtualizados: Partial<Usuario>) => Promise<boolean>;
  atualizarAvatar: (novaUrl: string) => void;
  requerAlteracaoSenha: boolean;
  setRequerAlteracaoSenha: (requer: boolean) => void;
  requerSenhaGmail: boolean;
  setRequerSenhaGmail: (requer: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  // Começa true: enquanto o token salvo ainda está sendo validado (chamada
  // assíncrona abaixo), 'usuario' é null mas isso NÃO significa "deslogado"
  // — sem essa distinção, rotas protegidas mandavam pro /login por um
  // instante em todo carregamento de página, mesmo com sessão válida.
  const [verificandoAutenticacao, setVerificandoAutenticacao] = useState<boolean>(true);
  const [pollingAtivo, setPollingAtivo] = useState<boolean>(false);
  const [requerAlteracaoSenha, setRequerAlteracaoSenha] = useState<boolean>(false);
  const [requerSenhaGmail, setRequerSenhaGmail] = useState<boolean>(false);

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      const resultado = await authService.login(email, senha);

      if (resultado.success && resultado.usuario) {
        const usuarioComAvatar = {
          ...resultado.usuario,
          avatar: resultado.usuario.avatar || '/profilepicture.png'
        };
        setUsuario(usuarioComAvatar);

        if (usuarioComAvatar.senha_alterada === false) {
          setRequerAlteracaoSenha(true);
        }

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      return false;
    }
  };

  const logout = () => {
    if (pollingAtivo) {
      pollingService.pararPolling('usuario');
      setPollingAtivo(false);
    }

    setUsuario(null);
    authService.logout();
  };

  const atualizarPerfil = async (dadosAtualizados: Partial<Usuario>): Promise<boolean> => {
    try {
      const sucesso = await authService.atualizarPerfil(dadosAtualizados);

      if (sucesso) {
        const resultado = await authService.obterUsuarioAtual();

        if (resultado.success && resultado.usuario) {
          const usuarioComAvatar = {
            ...resultado.usuario,
            avatar: resultado.usuario.avatar || '/profilepicture.png'
          };
          setUsuario(usuarioComAvatar);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      return false;
    }
  };

  // Atualiza apenas o avatar no estado (chamado após upload bem-sucedido)
  const atualizarAvatar = useCallback((novaUrl: string) => {
    setUsuario(prev => prev ? { ...prev, avatar: novaUrl } : prev);
  }, []);

  // Processar dados do usuário recebidos via polling
  const processarDadosUsuario = useCallback((dadosUsuario: any[]) => {
    if (!dadosUsuario || dadosUsuario.length === 0) return;

    const usuarioAtualizado = dadosUsuario[0];
    if (!usuarioAtualizado) return;

    setUsuario(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        ...usuarioAtualizado,
        avatar: usuarioAtualizado.avatar || prev.avatar || '/profilepicture.png'
      };
    });
  }, []);

  // Iniciar polling quando o usuário estiver autenticado
  useEffect(() => {
    if (usuario && !pollingAtivo) {
      pollingService.iniciarPolling('usuario', processarDadosUsuario, 180000);
      setPollingAtivo(true);
    } else if (!usuario && pollingAtivo) {
      pollingService.pararPolling('usuario');
      setPollingAtivo(false);
    }

    return () => {
      if (pollingAtivo) {
        pollingService.pararPolling('usuario');
      }
    };
  }, [usuario, pollingAtivo, processarDadosUsuario]);

  // Verificar autenticação ao iniciar
  useEffect(() => {
    const verificarAutenticacao = async () => {
      const usuarioSalvo = localStorage.getItem('bravobet_usuario');
      const token = localStorage.getItem('bravobet_token');

      if (usuarioSalvo && token) {
        try {
          const tokenValido = await authService.verificarToken();

          if (tokenValido) {
            const usuarioAtualizado = await authService.obterUsuarioAtual();
            if (usuarioAtualizado.success && usuarioAtualizado.usuario) {
              setUsuario({
                ...usuarioAtualizado.usuario,
                avatar: usuarioAtualizado.usuario.avatar || '/profilepicture.png'
              });
            } else {
              const usuarioParsed = JSON.parse(usuarioSalvo);
              setUsuario({
                ...usuarioParsed,
                avatar: usuarioParsed.avatar || '/profilepicture.png'
              });
            }
          } else {
            authService.logout();
          }
        } catch (error) {
          console.error('Erro ao verificar autenticação:', error);
          authService.logout();
        }
      }

      setVerificandoAutenticacao(false);
    };

    verificarAutenticacao();
  }, []);

  // Verificar se usuário precisa configurar senha do Gmail após autenticação
  useEffect(() => {
    const verificarSenhaGmail = async () => {
      if (usuario && !requerAlteracaoSenha) {
        try {
          const response = await api.get('/auth/me');
          if (response.data.success && response.data.usuario) {
            const usuarioAtualizado = response.data.usuario;
            const temSenhaGmail = usuarioAtualizado.senha_app_gmail && usuarioAtualizado.senha_app_gmail.trim() !== '';

            if (!temSenhaGmail) {
              setRequerSenhaGmail(true);
            }
          }
        } catch (error) {
          console.error('Erro ao verificar senha Gmail:', error);
        }
      }
    };

    verificarSenhaGmail();
  }, [usuario, requerAlteracaoSenha]);


  return (
    <AuthContext.Provider value={{
      usuario,
      login,
      logout,
      atualizarPerfil,
      atualizarAvatar,
      isAuthenticated: !!usuario,
      verificandoAutenticacao,
      requerAlteracaoSenha,
      setRequerAlteracaoSenha,
      requerSenhaGmail,
      setRequerSenhaGmail
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
